/**
 * Sección "Accesos de clientes" de un proyecto: quién puede verlo, estado de
 * cada invitación, reenvío y revocación. Revocar quita el permiso al instante
 * (la base deja de autorizar consultas y comentarios) sin borrar la cuenta.
 */
import { useState, useEffect, useCallback } from "react";
import { UserPlus, RefreshCw, Send, Ban, Loader, AlertCircle, Users, Check } from "lucide-react";
import InvitarClienteModal from "./InvitarClienteModal";
import {
  listarAccesos, reenviarInvitacion, revocarAcceso, ESTADOS_ACCESO,
} from "../../lib/accesos";
import { mensajeError } from "../../lib/comentarios";
import { fmtFecha, fmtFechaHora } from "../../lib/formato";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5", err:"#ef4444", errL:"#fee2e2", warn:"#f59e0b", warnL:"#fef3c7",
};
const th = { padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:C.sub,
  textTransform:"uppercase", letterSpacing:.4, borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" };
const td = { padding:"10px 12px", fontSize:13, color:C.text, borderBottom:`1px solid ${C.border}`, verticalAlign:"top" };

export default function AccesosClientes({ proyecto, proyectos = [], abrirInvitar = false, onCerrarInvitar }) {
  const [accesos,  setAccesos]  = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error,    setError]    = useState("");
  const [modal,    setModal]    = useState(abrirInvitar ? {} : null);
  const [ocupado,  setOcupado]  = useState(null);       // id en proceso
  const [aviso,    setAviso]    = useState(null);       // {tipo, texto}
  const [aRevocar, setARevocar] = useState(null);
  const [noInstalado, setNoInstalado] = useState(false); // la base aún no tiene el módulo

  const cargar = useCallback(async () => {
    setCargando(true); setError("");
    try { setAccesos(await listarAccesos(proyecto.id)); setNoInstalado(false); }
    catch (e) {
      if (e.codigo === "no_instalado") { setNoInstalado(true); setAccesos([]); }
      else setError(e.codigo ? e.message : mensajeError(e));
    }
    finally { setCargando(false); }
  }, [proyecto.id]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (abrirInvitar) setModal({}); }, [abrirInvitar]);

  const cerrarModal = () => { setModal(null); onCerrarInvitar?.(); };
  // Sin el módulo en la base no se abre el formulario: no podría enviar nada.
  useEffect(() => {
    if (noInstalado) { setModal(null); onCerrarInvitar?.(); }
  }, [noInstalado]); // eslint-disable-line react-hooks/exhaustive-deps

  async function reenviar(a) {
    setOcupado(a.id); setAviso(null);
    try {
      const r = await reenviarInvitacion(a.id);
      setAviso({ tipo:"ok", texto: `${r?.mensaje || "Invitación reenviada."} (${a.email})` });
    } catch (e) {
      setAviso({ tipo:"error", texto: e.message });
    } finally {
      setOcupado(null); cargar();
    }
  }

  async function confirmarRevocar() {
    const a = aRevocar;
    setOcupado(a.id); setAviso(null);
    try {
      await revocarAcceso(a.id, "Revocado desde el panel");
      setAviso({ tipo:"ok", texto: `Acceso revocado para ${a.email}. Su cuenta y sus otros proyectos no se modifican.` });
    } catch (e) {
      setAviso({ tipo:"error", texto: e.codigo ? e.message : mensajeError(e) });
    } finally {
      setOcupado(null); setARevocar(null); cargar();
    }
  }

  const activos = accesos.filter(a => a.estado === "aceptada").length;
  const pendientes = accesos.filter(a => a.estado === "pendiente").length;

  return (
    <div style={{flex:1, overflow:"auto", padding:"20px 24px"}}>
      <section aria-labelledby="accesos-titulo"
        style={{background:C.white, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:"0 1px 3px #0000000a"}}>
        <header style={{padding:"14px 18px", borderBottom:`1px solid ${C.border}`, display:"flex",
          alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap"}}>
          <div>
            <h3 id="accesos-titulo" style={{margin:0, fontSize:14, fontWeight:700, display:"flex", alignItems:"center", gap:7}}>
              <Users size={15} color={C.accent} aria-hidden="true"/> Accesos de clientes
            </h3>
            <div style={{fontSize:12, color:C.sub, marginTop:2}}>
              {activos} con acceso · {pendientes} invitación{pendientes !== 1 ? "es" : ""} pendiente{pendientes !== 1 ? "s" : ""}
            </div>
          </div>
          <div style={{display:"flex", gap:8}}>
            <button type="button" onClick={cargar} aria-label="Actualizar accesos"
              style={{display:"flex", alignItems:"center", gap:5, padding:"6px 11px", borderRadius:7,
                border:`1px solid ${C.border}`, background:C.white, color:C.sub, fontSize:12, cursor:"pointer",
                fontFamily:"'DM Sans',sans-serif"}}>
              <RefreshCw size={12} className={cargando ? "spinning" : ""}/> Actualizar
            </button>
            <button type="button" onClick={() => setModal({})} disabled={noInstalado}
              title={noInstalado ? "Disponible cuando el módulo esté instalado en el servidor" : undefined}
              style={{display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:8, border:"none",
                background:noInstalado ? C.light : C.accent, color:"#fff", fontWeight:700, fontSize:12,
                cursor:noInstalado ? "not-allowed" : "pointer", fontFamily:"'DM Sans',sans-serif"}}>
              <UserPlus size={13}/> Invitar cliente al portal
            </button>
          </div>
        </header>

        {noInstalado && (
          <div role="status" style={{margin:"12px 18px 0", padding:"10px 12px", borderRadius:8, fontSize:12,
            lineHeight:1.5, color:"#92400e", background:C.warnL, border:`1px solid ${C.warn}40`,
            display:"flex", gap:7, alignItems:"flex-start"}}>
            <AlertCircle size={14} style={{flexShrink:0, marginTop:1}}/>
            <span>
              <strong>Invitaciones aún no disponibles.</strong> El módulo de accesos de clientes todavía no está
              instalado en el servidor. El resto del proyecto funciona con normalidad.
            </span>
          </div>
        )}
        {aviso && (
          <div role={aviso.tipo === "error" ? "alert" : "status"}
            style={{margin:"12px 18px 0", padding:"9px 12px", borderRadius:8, fontSize:12, display:"flex", gap:7,
              alignItems:"center", color: aviso.tipo === "error" ? C.err : C.ok,
              background: aviso.tipo === "error" ? C.errL : C.okL}}>
            {aviso.tipo === "error" ? <AlertCircle size={13}/> : <Check size={13}/>} {aviso.texto}
          </div>
        )}
        {error && (
          <div role="alert" style={{margin:"12px 18px 0", padding:"9px 12px", borderRadius:8, fontSize:12,
            color:C.err, background:C.errL, display:"flex", gap:7, alignItems:"center"}}>
            <AlertCircle size={13}/> {error}
          </div>
        )}

        {cargando && !accesos.length ? (
          <div style={{padding:24, textAlign:"center", color:C.sub, fontSize:13}}>
            <Loader size={14} className="spinning"/> Cargando accesos…
          </div>
        ) : noInstalado ? null : !accesos.length && !error ? (
          <div style={{padding:"28px 18px", textAlign:"center", color:C.sub, fontSize:13}}>
            Nadie tiene acceso todavía. Usa “Invitar cliente al portal” para que tu cliente siga el avance y comente.
          </div>
        ) : (
          <div style={{overflowX:"auto", marginTop:8}}>
            <table style={{width:"100%", borderCollapse:"collapse", minWidth:760}}>
              <thead style={{background:C.bg}}>
                <tr>
                  {["Nombre", "Correo", "Proyecto", "Invitación", "Estado", "Último acceso", "Acciones"].map(h =>
                    <th key={h} style={th} scope="col">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {accesos.map(a => {
                  const est = ESTADOS_ACCESO[a.estado] || ESTADOS_ACCESO.pendiente;
                  const puedeReenviar = a.estado === "pendiente" || a.estado === "vencida";
                  const puedeRevocar = a.estado !== "revocada";
                  return (
                    <tr key={a.id} style={{opacity: a.estado === "revocada" ? .65 : 1}}>
                      <td style={td}>
                        <div style={{fontWeight:600}}>{a.nombre || "—"}</div>
                        {a.contacto_razon_social && <div style={{fontSize:11, color:C.sub}}>{a.contacto_razon_social}</div>}
                      </td>
                      <td style={{...td, wordBreak:"break-all"}}>{a.email}</td>
                      <td style={td}><div>{a.proyecto_nombre}</div><div style={{fontSize:11, color:C.light}}>{a.proyecto_id}</div></td>
                      <td style={{...td, fontSize:12, color:C.sub}}>
                        <div>{fmtFecha(a.invitado_at)}</div>
                        {a.estado === "pendiente" && <div>Vence {fmtFecha(a.expira_at)}</div>}
                        {a.ultimo_envio_estado === "fallido" && (
                          <div style={{color:C.err}} title={a.ultimo_envio_detalle || ""}>Correo no enviado</div>
                        )}
                        {a.envios > 1 && <div>{a.envios} envíos</div>}
                      </td>
                      <td style={td}>
                        <span style={{padding:"2px 9px", borderRadius:10, fontSize:11, fontWeight:700,
                          color:est.color, background:est.bg}}>{est.label}</span>
                        {a.aceptado_at && a.estado === "aceptada" && (
                          <div style={{fontSize:11, color:C.light, marginTop:3}}>desde {fmtFecha(a.aceptado_at)}</div>
                        )}
                      </td>
                      <td style={{...td, fontSize:12, color:C.sub}}>
                        {a.ultimo_acceso_at ? fmtFechaHora(a.ultimo_acceso_at) : a.estado === "aceptada" ? "Aún no entra" : "—"}
                      </td>
                      <td style={td}>
                        <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
                          {puedeReenviar && (
                            <button type="button" disabled={!!ocupado} onClick={() => reenviar(a)}
                              aria-label={`Reenviar invitación a ${a.email}`}
                              style={btnAccion(C.accent)}>
                              {ocupado === a.id ? <Loader size={11} className="spinning"/> : <Send size={11}/>} Reenviar
                            </button>
                          )}
                          {a.estado === "revocada" && (
                            <button type="button" disabled={!!ocupado}
                              onClick={() => setModal({ inicial: { nombre: a.nombre, email: a.email } })}
                              style={btnAccion(C.accent)}>
                              <UserPlus size={11}/> Invitar de nuevo
                            </button>
                          )}
                          {puedeRevocar && (
                            <button type="button" disabled={!!ocupado} onClick={() => setARevocar(a)}
                              aria-label={`Revocar acceso de ${a.email}`}
                              style={btnAccion(C.err)}>
                              <Ban size={11}/> Revocar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {aRevocar && (
        <div role="alertdialog" aria-modal="true" aria-labelledby="revocar-titulo"
          style={{position:"fixed", inset:0, zIndex:9998, background:"rgba(15,23,42,0.4)",
            display:"flex", alignItems:"center", justifyContent:"center", padding:16}}>
          <div style={{background:C.white, borderRadius:14, padding:"24px 26px", maxWidth:420, width:"100%",
            border:`1px solid ${C.border}`, boxShadow:"0 20px 60px #0000002a"}}>
            <h3 id="revocar-titulo" style={{margin:"0 0 8px", fontSize:16}}>¿Revocar acceso?</h3>
            <p style={{margin:"0 0 18px", fontSize:13, color:C.sub, lineHeight:1.6}}>
              <strong style={{color:C.text}}>{aRevocar.email}</strong> dejará de ver y comentar
              “{aRevocar.proyecto_nombre}” de inmediato. Su cuenta y su acceso a otros proyectos no se modifican.
            </p>
            <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
              <button type="button" onClick={() => setARevocar(null)} disabled={!!ocupado}
                style={{padding:"8px 18px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent",
                  color:C.sub, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>Cancelar</button>
              <button type="button" onClick={confirmarRevocar} disabled={!!ocupado}
                style={{padding:"8px 18px", borderRadius:8, border:"none", background:C.err, color:"#fff",
                  fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>
                {ocupado ? "Revocando…" : "Sí, revocar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && !noInstalado && (
        <InvitarClienteModal
          proyectos={proyectos.length ? proyectos : [proyecto]}
          proyectoInicial={proyecto.id}
          accesosActuales={accesos}
          inicial={modal.inicial}
          onCerrar={cerrarModal}
          onInvitado={cargar}/>
      )}
    </div>
  );
}

const btnAccion = (color) => ({
  display:"flex", alignItems:"center", gap:4, padding:"4px 9px", borderRadius:6,
  border:`1px solid ${color}40`, background:"transparent", color, fontSize:11, fontWeight:700,
  cursor:"pointer", fontFamily:"'DM Sans',sans-serif", whiteSpace:"nowrap",
});
